import React, { useMemo } from 'react';
import { Breadcrumbs as MantineBreadcrumbs, Anchor, Center } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconHome } from '@tabler/icons-react';

/**
 * Breadcrumb item
 */
interface BreadcrumbItem {
  label: string;
  path: string;
}

/**
 * Route label mappings
 */
const ROUTE_LABELS: Record<string, string> = {
  admin: 'Admin',
  dashboard: 'Dashboard',
  mentions: 'Mentions',
  businesses: 'Businesses',
  regions: 'Regions',
  users: 'Users',
  'my-business': 'My Business',
  create: 'Create',
  edit: 'Edit'
};

/**
 * Breadcrumbs Component
 *
 * Dynamically generates breadcrumb navigation based on current route
 */
export const Breadcrumbs: React.FC = (): React.ReactElement | null => {
  const location = useLocation();
  const navigate = useNavigate();

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathParts = location.pathname.split('/').filter(Boolean);

    // Don't show breadcrumbs on root or login pages
    if (pathParts.length === 0 || pathParts[0] === 'login') {
      return [];
    }

    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    pathParts.forEach((part) => {
      currentPath += `/${part}`;

      // Skip UUIDs (detail pages)
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part);

      if (isUuid) {
        items.push({
          label: 'Details',
          path: currentPath
        });
        return;
      }

      // Get label from mapping or capitalize part
      const label = ROUTE_LABELS[part] ?? part.charAt(0).toUpperCase() + part.slice(1);

      items.push({
        label,
        path: currentPath
      });
    });

    return items;
  }, [location.pathname]);

  const handleNavigate = (path: string): void => {
    navigate(path);
  };

  // Don't render breadcrumbs if empty
  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <MantineBreadcrumbs separator="/">
      {/* Home icon for root */}
      <Anchor onClick={(): void => handleNavigate('/')}>
        <Center display="inline-flex">
          <IconHome size={16} />
        </Center>
      </Anchor>

      {/* Breadcrumb items */}
      {breadcrumbs.map((item, _idx) => {
        const isLast = _idx === breadcrumbs.length - 1;

        return isLast ? (
          <span key={item.path}>{item.label}</span>
        ) : (
          <Anchor key={item.path} onClick={(): void => handleNavigate(item.path)}>
            {item.label}
          </Anchor>
        );
      })}
    </MantineBreadcrumbs>
  );
};
